import React, { useState } from "react";
import { Button, Card, Divider, Input, Typography, Alert, Flex, Space, Modal, Row, Col, Grid } from "antd";
import { MailOutlined, LockOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { AuthenticationService } from "../services/AuthenticationService";
import TopNavigation from "./snipets/TopNavigation";
import Footer from "./snipets/Footer";

const { Title, Text, Link } = Typography;
const { useBreakpoint } = Grid;

const LogInPage: React.FC = () => {
    const navigate = useNavigate();
    const screens = useBreakpoint();

    const [email,        setEmail]        = useState("");
    const [password,     setPassword]     = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading,      setLoading]      = useState(false);

    // After a successful login, try to get the user's location and route
    // them to /restaurantsPage/:lat/:lng. If permission is denied or
    // unavailable, show a modal explaining why instead of silently failing.
    const goToRestaurantsWithLocation = () => {
        if (!navigator.geolocation) {
            Modal.warning({
                title: "Location unavailable",
                content: "Your browser doesn't support location services. You can still browse, but nearby results may not be accurate.",
                onOk: () => navigate("/restaurantsPage"),
            });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                navigate(`/restaurantsPage/${lat}/${lng}`);
            },
            (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                    Modal.warning({
                        title: "Location access needed",
                        content: "Please enable location permissions so we can show restaurants near you.",
                        onOk: () => navigate("/restaurantsPage"),
                    });
                } else {
                    Modal.warning({
                        title: "Location unavailable",
                        content: "We couldn't determine your location right now. You can still browse and set your address manually.",
                        onOk: () => navigate("/restaurantsPage"),
                    });
                }
            }
        );
    };

    const handleLogin = () => {
        if (!email || !password) {
            setErrorMessage("Please enter both email and password.");
            return;
        }

        setErrorMessage("");
        setLoading(true);

        AuthenticationService.signIn({ email, password })
            .then((data: any) => {
                localStorage.setItem("access",  data.access);
                localStorage.setItem("refresh", data.refresh);
                localStorage.setItem("role",    data.role);

                // TODO: restore role-based routing once other flows are ready
                goToRestaurantsWithLocation();

                // if (data.role === "driver")       navigate("/driverOrdersPage");
                // else if (data.role === "seller")  navigate("/sellerDashboard");
                // else                              navigate("/serviceSelection");
            })
            .catch((error: any) => {
                const status = error?.response?.status || error?.status;
                if (status === 401)      setErrorMessage("Incorrect email or password.");
                else if (status === 403) setErrorMessage("Your account is not authorized.");
                else if (status === 404) setErrorMessage("No account found with this email.");
                else if (status >= 500)  setErrorMessage("Server error. Please try again later.");
                else                     setErrorMessage("Login failed. Please check your credentials.");
            })
            .finally(() => setLoading(false));
    };

    return (
        <div style={{ width: "100%", overflowX: "hidden" }}>
            <TopNavigation />

            <Row
                justify="center"
                align="middle"
                style={{
                    width: "100%",
                    margin: 0,
                    minHeight: "calc(100dvh - 64px)", // adjust 64px if your nav height differs
                    background: "#f5f5f0",
                    padding: screens.xs ? "24px 12px" : "40px 16px",
                }}
            >
                <Col xs={24} sm={20} md={14} lg={10} xl={8}>
                    <Card
                        variant="borderless"
                        styles={{
                            body: { padding: screens.xs ? 20 : 32 },
                        }}
                        style={{
                            width: "100%",
                            borderRadius: 12,
                            boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                        }}
                    >
                        {/* Header */}
                        <Flex vertical align="center" gap={4} style={{ marginBottom: 20 }}>
                            <Title level={4} style={{ margin: 0 }}>Sign In Quick Quick</Title>
                            <Text type="secondary">Sign in to continue</Text>
                        </Flex>

                        <Space direction="vertical" size="middle" style={{ width: "100%" }}>

                            <Input
                                prefix={<MailOutlined />}
                                placeholder="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                                size="large"
                                autoComplete="email"
                            />

                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                size="large"
                                autoComplete="current-password"
                                iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                onPressEnter={handleLogin}
                            />

                            <Flex justify="flex-end">
                                <Link onClick={() => !loading && navigate("/forgotPassword")}>Forgot Password?</Link>
                            </Flex>

                            {errorMessage && <Alert message={errorMessage} type="error" showIcon />}

                            <Button type="primary" size="large" block loading={loading} onClick={handleLogin}>
                                Login
                            </Button>
                        </Space>

                        <Divider plain><Text type="secondary" style={{ fontSize: 12 }}>or continue with</Text></Divider>

                        <Space direction="vertical" size="small" style={{ width: "100%" }}>
                            <Button
                                size="large"
                                block
                                disabled={loading}
                                onClick={() => {}}
                                icon={
                                    <img
                                        src="/googlr_logo.png"
                                        alt="Google"
                                        style={{ width: 18, height: 18, verticalAlign: "middle" }}
                                    />
                                }
                            >
                                Continue with Google
                            </Button>
                        </Space>

                        <Flex justify="center" gap={4} wrap="wrap" style={{ marginTop: 16, textAlign: "center" }}>
                            <Text type="secondary">Don't have an account?</Text>
                            <Link onClick={() => !loading && navigate("/register")}>Register</Link>
                        </Flex>

                    </Card>
                </Col>
            </Row>

            <Footer />
        </div>
    );
};

export default LogInPage;